"use client"

import React from "react"
import Link from "next/link"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"

interface ErrorPageProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function SoundDetailError({ error, reset }: ErrorPageProps) {
  React.useEffect(() => {
    import('@sentry/browser').then(({ captureException }) => {
      captureException(error, { tags: { context: 'SoundDetailErrorBoundary' } })
    }).catch(() => {})
  }, [error])

  return (
    <div className="relative min-h-screen overflow-hidden bg-[var(--tls-canvas)]">
      <div className="relative mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-lg rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[color:color-mix(in_srgb,var(--tls-surface)_94%,transparent)] px-6 py-8 text-center sm:px-8">
          {/* Bounded failure-surface exception keeps the contained ground,
              but the border and ground are ink/hairline, not red. */}
          <div className="mx-auto mb-5 flex size-16 items-center justify-center rounded-[var(--radius-pill)] border border-[color:var(--tls-border-strong)] bg-[var(--tls-surface-elevated)] text-[var(--tls-fg-strong)]">
            <AlertTriangle className="size-8" />
          </div>

          <div className="space-y-2">
            <h1 className="font-display text-2xl text-[var(--tls-fg-strong)]">
              Sound not available
            </h1>
            <p className="text-sm text-[var(--tls-fg-muted)] sm:text-base">
              We couldn&apos;t load this sound right now. This is usually temporary.
            </p>
          </div>

          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <Button onClick={reset} className="font-display">
              <RefreshCw className="size-4" />
              Try again
            </Button>
            <Button asChild variant="outline" className="font-display">
              <Link href="/sounds">
                Browse sounds
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
